/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  OCPPLogsInitialProperties,
  OCPPLogsResponseInterface,
  RequestOCPPLogsInterface,
} from '../../interface/ocpp-logs-interface';
import {
  ClearFilters,
  UpdateCommonFilter,
  RemoveSingleFilterFromList,
  ConfigureListFilterResponse,
  ReplaceValueInFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import ocppLogsApi from '../../utils/api/ocpp-logs';

export const fetchOCPPLogs = createAsyncThunk<OCPPLogsResponseInterface, RequestOCPPLogsInterface>(
  'ocpp-logs/fetchCpoOcpp',
  async (data: RequestOCPPLogsInterface, { dispatch }) => {
    try {
      const response = await ocppLogsApi.fetchOCPPLogsList(removeEmptyKeys(data));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// a thunk to refresh OCPP logs
export const refreshOCPPLogs = createAsyncThunk<
  OCPPLogsResponseInterface,
  RequestOCPPLogsInterface
>('ocpp-logs/refreshOCPPLogs', async (data: RequestOCPPLogsInterface) => {
  const response = await ocppLogsApi.fetchOCPPLogsList(removeEmptyKeys(data));
  return response.data;
});

export const fetchOCPPLogsFilters = createAsyncThunk<CommonFilterProperties[]>(
  'ocpp-logs/fetchOCpoOcppLogsFilters',
  async (_, { dispatch }) => {
    try {
      const response = await ocppLogsApi.fetchOCPPLogsFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });

      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: OCPPLogsInitialProperties = {
  ocppLogs: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

export const OcppLogSlice = createSlice({
  name: 'ocpp-logs',
  initialState,
  reducers: {
    /**
     * Update the device management filters
     * @param {any} state - The current state of the device management filters.
     * @param {any} action - The payload containing the updated filter.
     */
    updateOCPPLogsFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };

      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );

        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      } else if (payload.type === 'replace') {
        const { updatedCommonFilter, isFilterUpdated } = ReplaceValueInFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state.
     */
    disableOCPPLogsFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Function for sorting data by it's field
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * extraReducer logic
   * @param {any} builder - The builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<OCPPLogsInitialProperties>) => {
    builder
      .addCase(fetchOCPPLogs.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchOCPPLogs.fulfilled,
        (state: OCPPLogsInitialProperties, action: PayloadAction<OCPPLogsResponseInterface>) => {
          state.loading = false;
          state.ocppLogs = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchOCPPLogs.rejected, (state) => {
        state.loading = false;
      });
    builder.addCase(
      refreshOCPPLogs.fulfilled,
      (state: OCPPLogsInitialProperties, action: PayloadAction<OCPPLogsResponseInterface>) => {
        state.ocppLogs = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );
    builder
      .addCase(fetchOCPPLogsFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchOCPPLogsFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = action.payload.filter((filters) => filters.key !== 'startDate_endDate');
        },
      )
      .addCase(fetchOCPPLogsFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const { updateOCPPLogsFilters, disableOCPPLogsFiltersApplied, setSortData } =
  OcppLogSlice.actions;

export default OcppLogSlice.reducer;
